import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getCurrentUser, signIn as authSignIn, signOut as authSignOut, type AuthUser } from "../lib/auth";
import {
  DEMO_ROLES,
  formatRoleSummary,
  GOVSUITE_ONLY_ROLES,
  homePathForRoles,
  normalizeStoredRoleIds,
  rolesByIds,
  rolesVisibleForEmail,
  unionModuleIds,
  type DemoRole,
  type DemoRoleId,
} from "../lib/roles";

const ROLE_KEY = "govsuite.demo.roles";
const EMAIL_OVERRIDE_KEY = "govsuite.demo.email_override";

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  /** Active demo RoleGrant preview (one or more). */
  roles: DemoRole[];
  roleIds: DemoRoleId[];
  /** Union of granted module ids (DEC-NAV-001). */
  moduleIds: string[];
  roleSummary: string;
  homePath: string;
  /** @deprecated Prefer roles[0]; kept for narrow call sites. */
  role: DemoRole;
  /** Cognito email, or QA override when set (must be @govsuite.com). */
  effectiveEmail: string | null;
  emailOverride: string | null;
  setEmailOverride: (email: string | null) => void;
  setRoleIds: (ids: DemoRoleId[]) => void;
  toggleRoleId: (id: DemoRoleId) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

function readEmailOverride(): string | null {
  try {
    const v = localStorage.getItem(EMAIL_OVERRIDE_KEY);
    if (v && v.toLowerCase().endsWith("@govsuite.com")) return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailOverride, setEmailOverrideState] = useState<string | null>(() => readEmailOverride());
  const [roleIds, setRoleIdsState] = useState<DemoRoleId[]>(() =>
    normalizeStoredRoleIds(localStorage.getItem(ROLE_KEY)),
  );

  useEffect(() => {
    getCurrentUser()
      .then(setUser)
      .finally(() => setLoading(false));
  }, []);

  const effectiveEmail = emailOverride || user?.email || null;

  /** Strip GovSuite-only roles if email is not @govsuite.com. */
  useEffect(() => {
    const allowed = new Set(rolesVisibleForEmail(effectiveEmail).map((r) => r.id));
    setRoleIdsState((prev) => {
      const next = prev.filter((id) => allowed.has(id));
      if (next.length === prev.length) return prev;
      const safe = next.length ? next : (["ROLE-EMPLOYEE"] as DemoRoleId[]);
      localStorage.setItem(ROLE_KEY, JSON.stringify(safe));
      return safe;
    });
  }, [effectiveEmail]);

  const setEmailOverride = useCallback((email: string | null) => {
    if (!email) {
      localStorage.removeItem(EMAIL_OVERRIDE_KEY);
      setEmailOverrideState(null);
      return;
    }
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.endsWith("@govsuite.com")) {
      window.alert("Email override must end with @govsuite.com (GovSuite Support gate).");
      return;
    }
    localStorage.setItem(EMAIL_OVERRIDE_KEY, trimmed);
    setEmailOverrideState(trimmed);
  }, []);

  const setRoleIds = useCallback(
    (ids: DemoRoleId[]) => {
      const visible = new Set(rolesVisibleForEmail(effectiveEmail).map((r) => r.id));
      const next = ids.filter((id) => DEMO_ROLES.some((r) => r.id === id) && visible.has(id));
      const safe = next.length ? next : (["ROLE-EMPLOYEE"] as DemoRoleId[]);
      localStorage.setItem(ROLE_KEY, JSON.stringify(safe));
      setRoleIdsState(safe);
    },
    [effectiveEmail],
  );

  const toggleRoleId = useCallback(
    (id: DemoRoleId) => {
      if (GOVSUITE_ONLY_ROLES.includes(id)) {
        const email = emailOverride || user?.email || "";
        if (!email.toLowerCase().endsWith("@govsuite.com")) {
          window.alert("GovSuite Support is only available for @govsuite.com emails.");
          return;
        }
      }
      setRoleIdsState((prev) => {
        const has = prev.includes(id);
        let next: DemoRoleId[];
        if (has) {
          next = prev.filter((x) => x !== id);
          if (!next.length) next = [id];
        } else {
          next = [...prev, id];
        }
        localStorage.setItem(ROLE_KEY, JSON.stringify(next));
        return next;
      });
    },
    [emailOverride, user?.email],
  );

  const signIn = useCallback(async (email: string, password: string) => {
    const u = await authSignIn(email, password);
    setUser(u);
  }, []);

  const signOut = useCallback(() => {
    authSignOut();
    setUser(null);
  }, []);

  const roles = useMemo(() => rolesByIds(roleIds), [roleIds]);
  const moduleIds = useMemo(() => unionModuleIds(roles), [roles]);
  const roleSummary = useMemo(() => formatRoleSummary(roles), [roles]);
  const homePath = useMemo(() => homePathForRoles(roles), [roles]);

  const value = useMemo(
    () => ({
      user,
      loading,
      roles,
      roleIds,
      moduleIds,
      roleSummary,
      homePath,
      role: roles[0],
      effectiveEmail,
      emailOverride,
      setEmailOverride,
      setRoleIds,
      toggleRoleId,
      signIn,
      signOut,
    }),
    [
      user,
      loading,
      roles,
      roleIds,
      moduleIds,
      roleSummary,
      homePath,
      effectiveEmail,
      emailOverride,
      setEmailOverride,
      setRoleIds,
      toggleRoleId,
      signIn,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth requires AuthProvider");
  return ctx;
}
